// users/{uid}/notifications/{id} — in-app notifications, v1 (ruled
// 2026-09-05): the review event. Written ONLY by functions (the
// submitReview callable, Admin SDK), so a notification cannot be forged;
// the owner reads and marks read, nothing else (firestore.rules). The live
// unread count lands in notifStore, which the tab bar's badge and the
// room's bell share.
import { db } from "@/firebaseConfig";
import { noteFailure } from "@/utils/silentFailure";
import { notifStore } from "@/services/notifStore";

const col = (uid) => db.collection("users").doc(uid).collection("notifications");

const firestoreImpl = {
  /** live unread → notifStore.unread; returns the unsubscribe */
  watchUnread(uid) {
    if (!uid) return () => {};
    return col(uid).where("read", "==", false).onSnapshot(
      (s) => { notifStore.unread = s.size; },
      (e) => noteFailure("notifications.watchUnread", e),
    );
  },
  /** newest first; the inbox shows fifty */
  async list(uid, limit = 50) {
    if (!uid) return [];
    try {
      const s = await col(uid).orderBy("at", "desc").limit(limit).get();
      return s.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      noteFailure("notifications.list", e);
      return [];
    }
  },
  async markRead(uid, ids) {
    if (!uid || !ids || !ids.length) return;
    try {
      const b = db.batch();
      ids.forEach((id) => b.update(col(uid).doc(id), { read: true }));
      await b.commit();
    } catch (e) {
      noteFailure("notifications.markRead", e);
    }
  },
};

// Same DEV-ONLY escape hatch as engineDeployService: local mode has no
// functions, so the desk's local twin writes here and this device reads it.
const KEY = "engineNotifsLocal.v1";
const EV = "engine:notifs";
const readLocal = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } };
const writeLocal = (rows) => { localStorage.setItem(KEY, JSON.stringify(rows)); window.dispatchEvent(new Event(EV)); };
const LOCAL = process.env.VUE_APP_ENGINE_LOCAL === "true" && process.env.VUE_APP_USE_EMULATOR !== "true";

/** the local twin's write (the desk in local mode); a no-op against Firestore */
export function pushLocalNotification(n) {
  if (!LOCAL) return;
  const rows = readLocal();
  rows.unshift({ id: `n${Date.now()}${Math.floor(Math.random() * 1e4)}`, read: false, at: Date.now(), ...n });
  writeLocal(rows.slice(0, 200));
}

const localImpl = {
  watchUnread() {
    const sync = () => { notifStore.unread = readLocal().filter((n) => n.read === false).length; };
    sync();
    window.addEventListener(EV, sync);
    return () => window.removeEventListener(EV, sync);
  },
  async list(uid, limit = 50) { return readLocal().slice(0, limit); },
  async markRead(uid, ids) {
    const set = new Set(ids || []);
    writeLocal(readLocal().map((n) => (set.has(n.id) ? { ...n, read: true } : n)));
  },
};

export default LOCAL ? localImpl : firestoreImpl;
